/**
 * AudioPlayerBar - メディア再生コントロールバー (PoC frontend/src/components/AudioPlayerBar.tsx より移植)
 *
 * [ ▶/⏸ ] [ 00:25:10 / 01:42:00 ] [ ===プログレスバー=== ] [ 1.0x ]
 */

import { useCallback } from 'react';
import type { MediaPlayerState, MediaPlayerControls } from '@/hooks/useMediaPlayer';
import { formatSeconds } from '@/utils/audioTimestamp';

interface AudioPlayerBarProps {
  state: MediaPlayerState;
  controls: MediaPlayerControls;
}

export function AudioPlayerBar({ state, controls }: AudioPlayerBarProps) {
  const progress = state.duration > 0 ? (state.currentTime / state.duration) * 100 : 0;

  const handleProgressClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      controls.seekToTime(ratio * state.duration);
    },
    [controls, state.duration],
  );

  if (!state.isLoaded) return null;

  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-t border-gray-200 flex-shrink-0">
      <button
        onClick={controls.seekToPrevUtterance}
        className="w-6 h-6 flex items-center justify-center rounded text-gray-500 hover:text-gray-700 hover:bg-gray-200 flex-shrink-0 transition-colors"
        title="前の発話"
      >
        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M8.445 14.832A1 1 0 0010 14v-2.798l5.445 3.63A1 1 0 0017 14V6a1 1 0 00-1.555-.832L10 8.798V6a1 1 0 00-1.555-.832l-6 4a1 1 0 000 1.664l6 4z" />
        </svg>
      </button>

      <button
        onClick={controls.skipBackward}
        className="w-6 h-6 flex items-center justify-center rounded text-gray-500 hover:text-gray-700 hover:bg-gray-200 flex-shrink-0 transition-colors text-[10px] font-mono font-bold"
        title="-10秒"
      >
        -10
      </button>

      <button
        onClick={controls.togglePlayPause}
        className="w-7 h-7 flex items-center justify-center rounded-full bg-teal-500 hover:bg-teal-600 text-white flex-shrink-0 transition-colors"
        title={state.isPlaying ? '一時停止' : '再生'}
      >
        {state.isPlaying ? (
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        ) : (
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </button>

      <button
        onClick={controls.skipForward}
        className="w-6 h-6 flex items-center justify-center rounded text-gray-500 hover:text-gray-700 hover:bg-gray-200 flex-shrink-0 transition-colors text-[10px] font-mono font-bold"
        title="+10秒"
      >
        +10
      </button>

      <button
        onClick={controls.seekToNextUtterance}
        className="w-6 h-6 flex items-center justify-center rounded text-gray-500 hover:text-gray-700 hover:bg-gray-200 flex-shrink-0 transition-colors"
        title="次の発話"
      >
        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M11.555 5.168A1 1 0 0010 6v2.798L4.555 5.168A1 1 0 003 6v8a1 1 0 001.555.832L10 11.202V14a1 1 0 001.555.832l6-4a1 1 0 000-1.664l-6-4z" />
        </svg>
      </button>

      <span className="font-mono text-xs text-gray-500 flex-shrink-0 w-[110px]">
        {formatSeconds(state.currentTime)} / {formatSeconds(state.duration)}
      </span>

      <div
        className="flex-1 h-1.5 bg-gray-200 rounded-full cursor-pointer group relative"
        onClick={handleProgressClick}
      >
        <div
          className="h-full bg-teal-400 rounded-full transition-[width] duration-100 relative"
          style={{ width: `${progress}%` }}
        >
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-teal-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>

      <button
        onClick={controls.cyclePlaybackRate}
        className="text-xs font-mono text-gray-500 hover:text-gray-700 px-1.5 py-0.5 rounded bg-gray-100 hover:bg-gray-200 flex-shrink-0 transition-colors"
        title="再生速度"
      >
        {state.playbackRate.toFixed(1)}x
      </button>
    </div>
  );
}
